/**
 * Auth Domain Types
 */

/**
 * JWT Token response from Strapi
 */
export interface AuthTokens {
  jwt: string
  expiresAt?: number
}

/**
 * User role from Strapi users-permissions plugin
 */
export interface UserRole {
  id: number
  documentId?: string
  name: string
  description?: string
  type: string
}

/**
 * Authenticated user from Strapi
 */
export interface AuthUser {
  id: number
  documentId: string
  username: string
  email: string
  confirmed: boolean
  blocked: boolean
  attemptBlocked?: boolean
  createdAt: string
  updatedAt: string
  provider?: string
  tenantId?: string
  fullName?: string
  phone?: string
  companyName?: string
  tenant?: TenantSummary
  role?: UserRole
  /** Location of the factory assigned to this user (security_guard only) */
  factoryLocation?: { documentId: string } | null
  /** Direct user→location (fallback when no factory) */
  userLocation?: { documentId: string } | null
  /** Factory documentId for factory-scoped roles (scale_operator, scale_admin, safety_manager, etc.) */
  factoryDocumentId?: string | null
}

/**
 * Auth state status
 */
export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error'

/**
 * Loading state for specific auth operations
 */
export interface AuthLoadingState {
  login: boolean
  register: boolean
  logout: boolean
  passwordReset: boolean
  sessionCheck: boolean
  profileUpdate: boolean
  changePassword: boolean
  changePasswordOtp: boolean
}

/**
 * Channel type for tenant
 */
export type TenantChannel = 'b2b' | 'ecommerce'

/**
 * Tenant summary for the authenticated user
 */
export interface TenantSummary {
  id: number | string
  documentId?: string
  name?: string
  code?: string
  tenantStatus?: string
  channel?: TenantChannel
}

/**
 * Auth state
 */
export interface AuthState {
  user: AuthUser | null
  token: string | null
  status: AuthStatus
  error: string | null
  isInitialized: boolean
  isAttemptBlocked: boolean
  loginAttemptsRemaining: number | null
  registrationStatus: 'idle' | 'submitting' | 'success' | 'error'
  registrationMessage: string | null
  passwordResetStatus: 'idle' | 'requesting' | 'success' | 'error'
  passwordResetMessage: string | null
  changePasswordStatus: 'idle' | 'submitting' | 'success' | 'error'
  changePasswordMessage: string | null
  changePasswordOtpStatus: 'idle' | 'sending' | 'success' | 'error'
  changePasswordOtpMessage: string | null
  loading: AuthLoadingState
}

export type ChangePasswordVerificationMethod = 'current_password' | 'email_otp' | 'phone_otp'

export type ChangePasswordOtpChannel = 'email' | 'phone'

/**
 * Login credentials
 */
export interface LoginCredentials {
  identifier: string // email or username
  password: string
  rememberMe?: boolean
  turnstileToken?: string
}

/**
 * Registration data
 */
export interface RegisterData {
  username: string
  email: string
  password: string
  fullName: string
  companyName: string
  mobilePhone: string
  telephone?: string
  vatNumber: string
  crNumber: string
  houseNumber?: string
  addressLine1: string
  city?: string
  region?: string
  postalCode?: string
  vatCertificate: File
  crDocument: File
  country?: string
  sapCustomerGroup: string
}

/**
 * Registration response payload
 */
export interface RegisterResult {
  status: string
  message: string
  user: AuthUser
  tenant?: TenantSummary
}

/**
 * Password reset request
 */
export interface ForgotPasswordRequest {
  email: string
}

/**
 * Password reset confirmation
 */
export interface ResetPasswordRequest {
  code: string
  password: string
  passwordConfirmation: string
}

/**
 * Delete account request
 */
export interface DeleteAccountRequest {
  deletionReason?: string
}

/**
 * Profile update request (customer portal settings)
 */
export interface UpdateProfileRequest {
  companyName: string
  phone: string
}

/**
 * Change password request for the authenticated user.
 */
export type ChangePasswordRequest =
  | {
      verificationMethod: 'current_password'
      currentPassword: string
      password: string
      passwordConfirmation: string
    }
  | {
      verificationMethod: 'email_otp'
      otpCode: string
      password: string
      passwordConfirmation: string
    }
  | {
      verificationMethod: 'phone_otp'
      otpCode: string
      password: string
      passwordConfirmation: string
    }

export interface ChangePasswordOtpRequest {
  channel: ChangePasswordOtpChannel
  locale?: string
}

/**
 * Password reset response
 */
export interface ForgotPasswordResult {
  ok: boolean
  message: string
}

/**
 * Reset password response  
 */
export interface ResetPasswordResult {
  jwt?: string
  user?: AuthUser
  message: string
}

/**
 * Delete account response
 */
export interface DeleteAccountResult {
  ok: boolean
  message: string
}

/**
 * Profile update response
 */
export interface UpdateProfileResult {
  user: AuthUser
}

/**
 * Change password response.
 */
export interface ChangePasswordResult {
  jwt: string
  user: AuthUser
  message: string
}

export interface ChangePasswordOtpResult {
  ok: boolean
  channel: ChangePasswordOtpChannel
  message: string
}

/**
 * Storage keys for auth
 */
export const AUTH_STORAGE_KEYS = {
  TOKEN: 'spcc-auth-token',
  USER: 'spcc-auth-user',
} as const
